import * as React from 'react';
import {Icon, Tabs} from '@forecast-it/design-system';

export interface TabDef {
	id: string;
	label: string;
	/** Rendered inside the label as `Comments (2)`; omit (or pass null) for no count. */
	count?: number | null;
}

interface Props {
	activeTab: string;
	onTabChange?: (tabId: string) => void;
	tabs?: TabDef[];
	/** The active tab's panel. Rendered inside the design system's own tab panel. */
	children?: React.ReactNode;
}

const DEFAULT_TABS: TabDef[] = [
	{id: 'overview', label: 'Overview'},
	{id: 'activity', label: 'Activity'},
	{id: 'stream', label: 'Stream'},
	{id: 'attachments', label: 'Attachments'},
	{id: 'tasks', label: 'Tasks'},
	{id: 'related', label: 'Related'},
];

// One glyph per tab, so the bar is scannable before the labels are read. `time`
// shares the clock with nothing else; `tasks` and `related` take the design
// system's own task and shared-item glyphs.
const TAB_ICONS: Record<string, string> = {
	overview: 'info',
	activity: 'activities',
	stream: 'feedback',
	attachments: 'paperclip',
	time: 'clock',
	tasks: 'task',
	related: 'shared',
};

/**
 * The detail page's tab bar and the active tab's panel.
 *
 * Wraps the design system's `Tabs`, which owns the tablist semantics, the
 * underline and the panel. Only the active panel is mounted, so `children` is
 * the caller's already-selected body rather than one child per tab.
 *
 * The count rides inside the label (`Stream (2)`) rather than in a separate
 * pill: the design keeps the bar to one type size, and a count that reads as
 * part of the label cannot be mistaken for a control.
 */
export function TicketTabs({activeTab, onTabChange, tabs = DEFAULT_TABS, children}: Props) {
	return (
		<Tabs value={activeTab} onValueChange={value => onTabChange?.(value)}>
			<Tabs.List>
				{tabs.map(tab => (
					<Tabs.Tab key={tab.id} value={tab.id} data-cy={`ticket-tab-${tab.id}`}>
						<Icon icon={TAB_ICONS[tab.id] ?? 'info'} size="s" />
						{tab.count != null ? `${tab.label} (${tab.count})` : tab.label}
					</Tabs.Tab>
				))}
			</Tabs.List>
			<Tabs.Content value={activeTab}>{children}</Tabs.Content>
		</Tabs>
	);
}
